import { AlertTriangle, CheckCircle2, CircleDashed, Loader2, ShieldAlert } from "lucide-react";
import { cn } from "@/lib/utils";

export function ScoreRing({ score = 0, size = 56, testId = "match-score-ring" }) {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const tone = score >= 85 ? "hsl(var(--brand))" : score >= 65 ? "hsl(var(--warn))" : "hsl(var(--destructive))";
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid={testId} aria-label={`Match score ${score}`}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth="5" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={tone} strokeWidth="5" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (c * Math.min(score, 100)) / 100} style={{ transition: "stroke-dashoffset .6s ease" }} />
      </svg>
      <span className="absolute inset-0 grid place-items-center font-display text-sm font-bold">{score}</span>
    </div>
  );
}

const PILL = {
  approved: ["bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/25", CheckCircle2],
  confirmed: ["bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/25", CheckCircle2],
  healthy: ["bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/25", CheckCircle2],
  active: ["bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/25", CheckCircle2],
  passed: ["bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/25", CheckCircle2],
  review_required: ["bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/25", AlertTriangle],
  unconfirmed: ["bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/25", AlertTriangle],
  degraded: ["bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/25", AlertTriangle],
  verified: ["bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/25", CheckCircle2],
  running: ["bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/25", Loader2],
  blocked: ["bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/25", ShieldAlert],
  failed: ["bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/25", ShieldAlert],
  down: ["bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/25", ShieldAlert],
  disabled: ["bg-muted text-muted-foreground border-border", CircleDashed],
};

export function StatusPill({ status, label, testId }) {
  const [cls, Icon] = PILL[status] || ["bg-muted text-muted-foreground border-border", CircleDashed];
  return (
    <span data-testid={testId} className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium", cls)}>
      <Icon className={cn("h-3 w-3", status === "running" && "animate-spin")} aria-hidden />
      {label || String(status || "unknown").replace(/_/g, " ")}
    </span>
  );
}

export function PageHeader({ eyebrow, title, children, testId }) {
  return (
    <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between fade-up">
      <div>
        {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
        <h1 data-testid={testId} className="text-3xl font-bold sm:text-4xl">{title}</h1>
      </div>
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

export function Empty({ icon: Icon, title, children, testId }) {
  return (
    <div data-testid={testId} className="panel flex flex-col items-start gap-3 p-8">
      {Icon && <Icon className="h-6 w-6 text-muted-foreground" aria-hidden />}
      <p className="font-display text-lg font-semibold">{title}</p>
      <div className="text-sm text-muted-foreground">{children}</div>
    </div>
  );
}

export function Loading({ label = "Loading" }) {
  return (
    <div className="flex items-center gap-2 p-8 text-sm text-muted-foreground" role="status" data-testid="loading-indicator">
      <Loader2 className="h-4 w-4 animate-spin" /> {label}…
    </div>
  );
}
